import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/** Buy Now 等待自动带码的上限，超了就直接放行去结账 */
const BUY_NOW_DISCOUNT_TIMEOUT = 2500;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // UG 加购只要 loading，成功后回到原文案，不走主题 Added
    if (!this.refs.addToCartButton?.classList.contains('ug-add-to-cart-button')) {
      this.animateAddToCart();
    }

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** 请求进行中禁止连点 */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /** bfcache 返回时解开 Buy Now 的 disabled */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.querySelectorAll('.ug-buy-now-button[data-loading="true"], .ug-buy-now-button[aria-busy="true"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = false;
      button.removeAttribute('data-loading');
      button.removeAttribute('aria-busy');
    });
    this.#submitting = false;
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);
    // 独立 Buy Now：按当前变体与数量直达结账，勾选的推荐商品一并带上
    if (submitter?.name === 'checkout' || submitter?.classList.contains('ug-buy-now-button')) {
      event.preventDefault();
      // 主加购进行中或自动带码 hold 时不要再进车，否则会重复下单
      if (this.#submitting || window.UGAutoDiscountBridge?.holdDrawer) return;
      this.#handleBuyNow(submitter);
      return;
    }
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (addToCartButton?.disabled || addToCartButton?.hasAttribute('disabled') || this.#submitting) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const relatedIds = this.#getSelectedRelatedVariantIds();
    const mainCount = this.#getSubmittedItemCount(formData);
    const itemCount = mainCount + relatedIds.length;
    const trackingProperties = this.#getSubmittedTrackingProperties(formData);
    const mainVariantId = formData.get('id');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const sectionsParam = cartItemComponentsSectionIds.join(',');
    formData.append('sections', sectionsParam);

    const fetchCfg = fetchConfig('javascript', { body: formData });
    this.#submitting = true;
    if (addToCartButton) {
      addToCartButton.setAttribute('aria-busy', 'true');
      // 商详主加购走 Added，不再叠一层 loading 转圈
      if (addToCartButton.classList.contains('ug-add-to-cart-button')) {
        addToCartButton.setAttribute('data-loading', 'true');
      }
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount,
              productId: this.dataset.productId,
              productTitle: this.dataset.productTitle,
              variantId: formData.get('id')?.toString(),
              trackingProperties,
            })
          );

          return;
        }

        const id = formData.get('id') || mainVariantId;

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (!id) throw new Error('Form ID is required');

        let sections = response.sections;
        let relatedFailed = false;
        if (relatedIds.length) {
          const relatedResult = await this.#addSelectedRelatedItems(relatedIds, sectionsParam);
          if (relatedResult?.ok) {
            // sections 有内容才覆盖；空对象不能把主商品抽屉快照冲掉
            if (relatedResult.sections && Object.keys(relatedResult.sections).length) {
              sections = relatedResult.sections;
            }
          } else {
            relatedFailed = true;
            this.#showFormError(
              relatedResult?.message || this.dataset.relatedAddError || Theme.translations.add_to_cart_error || ''
            );
          }
        }

        // Add aria-live region to inform screen readers that the item was added
        if (addToCartButton && !relatedFailed) {
          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: relatedFailed ? mainCount : itemCount,
            productId: this.dataset.productId,
            productTitle: this.dataset.productTitle,
            variantId: id.toString(),
            trackingProperties,
            sections,
            discountCodes: this.#getSelectedDiscountCodes(!relatedFailed),
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#submitting = false;
        if (addToCartButton) {
          addToCartButton.removeAttribute('data-loading');
          addToCartButton.removeAttribute('aria-busy');
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Buy Now：先加到当前购物车再跳结账。
   *
   * 不能用 cart permalink（/cart/{variant}:{qty}?checkout）——它的语义是替换整车，
   * 顾客已加的商品会被清空；而且绕过购物车，自动折扣码不会生效，展示到手价与结账价对不上。
   * @param {HTMLButtonElement} submitter
   */
  async #handleBuyNow(submitter) {
    const form = this.querySelector('form');
    if (!form) return;

    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '');
    if (!variantId) return;
    const quantity = Number(formData.get('quantity')) || 1;

    /** @type {{ id: number | string, quantity: number }[]} */
    const items = [{ id: Number(variantId) || variantId, quantity }];
    for (const relatedId of this.#getSelectedRelatedVariantIds()) {
      items.push({ id: Number(relatedId) || relatedId, quantity: 1 });
    }

    this.#submitting = true;
    submitter.setAttribute('data-loading', 'true');
    submitter.setAttribute('aria-busy', 'true');
    submitter.disabled = true;

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const result = await response.json();
      if (result?.status) throw new Error(result.description || result.message || '');

      await this.#applyBuyNowDiscountCodes();

      const root = window.Shopify?.routes?.root || '/';
      window.location.href = `${root.endsWith('/') ? root : `${root}/`}checkout`;
    } catch (error) {
      console.error(error);
      this.#submitting = false;
      submitter.removeAttribute('data-loading');
      submitter.removeAttribute('aria-busy');
      submitter.disabled = false;
      this.#showFormError(
        error instanceof Error && error.message ? error.message : Theme.translations.add_to_cart_error || ''
      );
    }
  }

  /**
   * 跳结账前把自动折扣码写到车上。带码逻辑归 ug-auto-discount 一处维护。
   * 重试链最长可能跑一两秒，超时就先放人走——码写不上也不能把顾客卡在按钮上。
   */
  async #applyBuyNowDiscountCodes() {
    const codes = this.#getSelectedDiscountCodes();
    const applyCodes = window.UGAutoDiscountBridge?.applyCodes;
    if (!codes.length || typeof applyCodes !== 'function') return;

    try {
      await Promise.race([applyCodes(codes), new Promise((resolve) => setTimeout(resolve, BUY_NOW_DISCOUNT_TIMEOUT))]);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {string} message
   */
  #showFormError(message) {
    const { addToCartTextError } = this.refs;
    if (!message) return;

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        addToCartTextError.appendChild(document.createTextNode(message));
      }
    }

    this.#setLiveRegionText(message);
    this.#timeout = setTimeout(() => {
      if (addToCartTextError) addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, 10000);
  }

  /** 商详推荐勾选的宿主；只认当前 section，QV / 购物车 / 对比表不加推荐 */
  #getRelatedScope() {
    if (this.closest('#quick-add-dialog, quick-add-dialog, .quick-add-modal')) return null;
    const formSection = this.closest('.shopify-section');
    if (formSection?.querySelector('.ug-related-product-checkbox')) return formSection;
    return null;
  }

  /**
   * 读取当前商详里勾选的推荐商品变体
   * @returns {string[]}
   */
  #getSelectedRelatedVariantIds() {
    const section = this.#getRelatedScope();
    if (!section) return [];
    return Array.from(section.querySelectorAll('.ug-related-product-checkbox:checked'))
      .map((input) => (input instanceof HTMLInputElement ? input.value : ''))
      .filter(Boolean);
  }

  /**
   * 主商品 + 勾选推荐上要自动带的折扣码
   * @param {boolean} [includeRelated]
   * @returns {string[]}
   */
  #getSelectedDiscountCodes(includeRelated = true) {
    /** @type {string[]} */
    const codes = [];
    const main = (this.getAttribute('data-ug-discount-code') || '').trim();
    if (main) codes.push(main);

    const section = includeRelated ? this.#getRelatedScope() : null;
    if (section) {
      section.querySelectorAll('.ug-related-product-checkbox:checked').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const code = (input.getAttribute('data-ug-discount-code') || '').trim();
        if (code) codes.push(code);
      });
    }

    return [...new Set(codes)];
  }

  /**
   * 主商品加购成功后再加勾选的推荐，避免改 FormData 丢掉 id 导致购物车弹不出来
   * @param {string[]} relatedIds
   * @param {string} sections
   * @returns {Promise<{ok: boolean, sections?: Record<string, string>, message?: string}>}
   */
  async #addSelectedRelatedItems(relatedIds, sections) {
    if (!relatedIds.length) return { ok: true };
    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: relatedIds.map((id) => ({ id: Number(id) || id, quantity: 1 })),
            sections,
          }),
        })
      );
      const json = await response.json();
      // 有 status 才是加购失败；sections 只是抽屉 HTML，缺了不代表货没进车
      if (json && !json.status) return { ok: true, sections: json.sections || {} };
      return {
        ok: false,
        message: json?.description || json?.message || '',
      };
    } catch (error) {
      console.error(error);
      return { ok: false };
    }
  }

  /**
   * Gets submitted quantity, falling back to one for product forms without quantity inputs.
   * @param {FormData} formData
   * @returns {number}
   */
  #getSubmittedItemCount(formData) {
    const submittedQuantity = Number(formData.get('quantity'));
    const defaultQuantity = Number(this.dataset.quantityDefault);
    const itemCount = submittedQuantity || defaultQuantity || 1;

    return Number.isFinite(itemCount) && itemCount > 0 ? itemCount : 1;
  }

  /**
   * Gets hidden tracking properties submitted with the line item.
   * @param {FormData} formData
   * @returns {{source: string, module: string, position: string}}
   */
  #getSubmittedTrackingProperties(formData) {
    return {
      source: formData.get('properties[_source]')?.toString() || '',
      module: formData.get('properties[_module]')?.toString() || '',
      position: formData.get('properties[_position]')?.toString() || '',
    };
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    if (!currentAddToCartButton) return;

    const isUnavailable = event.detail.resource == null || event.detail.resource.available === false;

    // Update the button state
    if (isUnavailable) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
    this.#syncPurchaseAvailability(isUnavailable);

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // 吸底栏等多 ATC：跳过 morph，由业务侧自行同步文案，避免错绑主表单拖慢/弄坏按钮
    if (this.hasAttribute('data-skip-variant-morph')) {
      currentAddToCartButton.setAttribute('data-loading', 'false');
      this.#syncAutoDiscountAttrs(event);
      return;
    }

    // 仅按当前表单 id 精确匹配；匹配不到就不 morph（绝不用页面第一个 ATC 兜底）
    const currentFormId = this.querySelector('form')?.id;
    const newHtml = event.detail.data.html;
    this.#syncAutoDiscountAttrs(event);
    if (!currentFormId || !newHtml) return;

    const escapedFormId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(currentFormId)
        : currentFormId;
    let newAddToCartButton = newHtml.querySelector(`#${escapedFormId} [ref="addToCartButton"]`);
    if (!newAddToCartButton && this.dataset.productId) {
      const escapedProductId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(this.dataset.productId)
          : this.dataset.productId;
      newAddToCartButton = newHtml.querySelector(
        `product-form-component[data-product-id="${escapedProductId}"] [ref="addToCartButton"]`
      );
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    this.#syncPurchaseAvailability(isUnavailable);
  };

  /**
   * 变体切换后同步自动带码属性：有变体商品跟当前变体，无变体商品跟商品级
   * @param {VariantUpdateEvent} event
   */
  #syncAutoDiscountAttrs(event) {
    const currentFormId = this.querySelector('form')?.id;
    const newHtml = event.detail?.data?.html;
    if (!currentFormId || !newHtml) return;

    const escapedFormId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(currentFormId)
        : currentFormId;
    const newHost = newHtml.querySelector(`#${escapedFormId}`)?.closest('product-form-component');
    if (!(newHost instanceof HTMLElement)) return;

    const code = (newHost.getAttribute('data-ug-discount-code') || '').trim();
    if (code) {
      this.setAttribute('data-ug-discount-code', code);
      this.setAttribute('data-ug-auto-discount', 'true');
    } else {
      this.removeAttribute('data-ug-discount-code');
      this.removeAttribute('data-ug-auto-discount');
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
    this.querySelectorAll('.ug-buy-now-button').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = true;
    });
  };

  /** Buy Now / 钱包跟 ATC 同一套有货缺货；有货要还原加购文案，不能留着 Sold Out */
  #syncPurchaseAvailability(unavailable) {
    this.querySelectorAll('.ug-buy-now-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = Boolean(unavailable);
      if (unavailable) {
        button.removeAttribute('data-loading');
        button.removeAttribute('aria-busy');
      }
    });

    this.querySelector('.product-form-buttons')?.classList.toggle('is-variant-unavailable', Boolean(unavailable));
    this.querySelectorAll('.ug-product-buy-now, .ug-product-checkout-divider, .ug-product-checkout').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = Boolean(unavailable);
    });

    const atcButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    atcButton?.classList.toggle('is-sold-out', Boolean(unavailable));
    const textEl = atcButton?.querySelector('.add-to-cart-text__content');
    const label = unavailable
      ? this.getAttribute('data-sold-out-text')
      : this.getAttribute('data-add-to-cart-text');
    if (label && textEl) textEl.textContent = label;

    this.querySelectorAll('quantity-selector-component').forEach((el) => {
      if (typeof el.setUnavailable === 'function') el.setUnavailable(unavailable);
    });
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
